/**
 * The builder for dashboards and reports: a full-screen editor over one document. Edits go
 * into an undo history, auto-save a moment later with a revision check, and
 * the page's widgets are queried live (or from sample data) as you build.
 */

import { useCallback, useState, type ReactNode } from 'react';

import {
  BuilderBanners,
  BuilderBar,
  SHARE_COPY_NOTICE,
  DEFAULT_SETTINGS,
  useUndoKeys,
  type Panel,
} from './BuilderBar.tsx';
import { BuilderPanels } from './BuilderPanels.tsx';
import { BuilderScope } from './BuilderScope.tsx';
import { PAGE_FORMAT, stackForMobile } from './grid.ts';
import { queryOf, useAutosave, useHistory, usePeriod, useQueries } from './hooks.ts';
import { AddWidgetButton, EmptyPageHint } from './Library.tsx';
import { coverFacts } from './PageArt.tsx';
import { PageTabs } from './PageTabs.tsx';
import { PageView, PresentReport } from './PageView.tsx';
import type { Branding } from '../portal/portal-api.ts';
import { agencyApi } from './agency-api.ts';
import { ReportPages } from './ReportPages.tsx';
import { RollupBar } from './RollupBar.tsx';
import { useRollupState } from './rollup-context.ts';
import type { ReportTheme } from './report-theme.ts';
import type { Catalogue, DocumentSettings, Page, ReportDocument, Widget } from './reports-api.ts';
import { ThemeScope } from './ThemeScope.tsx';
import { usePasteKey, useWidgetActions } from './use-widget-actions.ts';
import { WidgetMenu } from './WidgetMenu.tsx';
import { widgetDownload } from './xlsx-downloads.ts';

export function Builder({
  organisationId,
  catalogue,
  initial,
  client,
  branding,
  onClose,
  onOpenDocument,
}: {
  organisationId: string;
  catalogue: Catalogue;
  initial: { document: ReportDocument; pages: Page[] };
  client: { name: string; website: string | null };
  branding: Branding;
  onClose: () => void;
  onOpenDocument: (documentId: string) => void;
}): ReactNode {
  const [document, setDocument] = useState(initial.document);
  const [name, setName] = useState(initial.document.name);
  const [settings, setSettings] = useState<DocumentSettings>({
    ...DEFAULT_SETTINGS,
    ...initial.document.settings,
  });
  const [theme, setTheme] = useState(initial.document.theme as ReportTheme);
  const history = useHistory(initial.pages);
  const [pageIndex, setPageIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [preview, setPreview] = useState(false);
  const [presenting, setPresenting] = useState(false);
  const [notice, setNotice] = useState<string | null>(
    initial.document.share_of ? SHARE_COPY_NOTICE : null,
  );
  const format = PAGE_FORMAT[initial.document.kind];
  const isReport = format !== null;
  const noun = isReport ? 'report' : 'dashboard';

  const pages = history.pages;
  const page = pages[Math.min(pageIndex, pages.length - 1)] as Page;
  const autoAlign = settings.auto_align !== false;
  const editable = !preview && device === 'desktop';
  const shown = device === 'mobile' ? stackForMobile(page.widgets) : page.widgets;
  const keyOf = useCallback((index: number) => shown[index]?.id ?? `w${index}`, [shown]);

  const query = queryOf(settings);
  const rollup = useRollupState(organisationId, document, settings, setSettings);
  const save = useAutosave({
    organisationId,
    document,
    name,
    settings,
    theme: theme as Record<string, unknown>,
    pages,
    onSaved: (saved, savedPages, sentPages) => {
      setDocument(saved);
      // Adopt server ids only if nothing changed while the save was in flight.
      if (sentPages === history.pages) history.replace(savedPages);
    },
  });
  const { results, error } = useQueries({
    organisationId,
    businessUnitId: document.business_unit_id,
    clientIds: rollup?.clientIds,
    ...query,
    widgets: shown,
    keyOf,
  });

  const setWidgets = useCallback(
    (widgets: Widget[]) =>
      history.set(pages.map((item, index) => (index === pageIndex ? { ...item, widgets } : item))),
    [history, pages, pageIndex],
  );
  const period = usePeriod({ organisationId, businessUnitId: document.business_unit_id, ...query });
  const cover = coverFacts({
    reportName: name,
    clientName: client.name,
    clientUrl: client.website,
    period,
    preset: query.range.preset,
    branding,
  });
  const shareWithClient = (next: boolean) =>
    agencyApi.share(
      organisationId,
      document.id,
      next,
      (shared_at) => setDocument((current) => ({ ...current, shared_at })),
      setNotice,
    );

  const widgetActions = useWidgetActions({
    catalogue,
    page,
    maxRows: format?.rows ?? null,
    autoAlign,
    setWidgets,
    setSelected,
    setPanel,
    setNotice,
    pages,
    pageIndex,
    setPages: history.set,
  });

  useUndoKeys(history);
  usePasteKey(widgetActions.paste);
  const selectPage = (index: number) => {
    setPageIndex(index);
    setSelected(null);
  };
  const changePages = (next: Page[], nextActive?: number) => {
    history.set(next);
    if (nextActive !== undefined) setPageIndex(nextActive);
  };
  const isGridPage = (page.settings.layout ?? 'grid') === 'grid';

  return (
    <BuilderScope compare={settings.compare} rollup={rollup}>
      <div className="rb" role="dialog" aria-label={`Editing ${name}`}>
        <BuilderBar
          noun={noun}
          name={name}
          onName={setName}
          settings={settings}
          onSettings={setSettings}
          history={history}
          isReport={isReport}
          device={device}
          onDevice={setDevice}
          panel={panel}
          onPanel={setPanel}
          preview={preview}
          onPreview={setPreview}
          onPresent={() => setPresenting(true)}
          shared={document.scope === 'CLIENT' ? Boolean(document.shared_at) : null}
          onShare={(next) => void shareWithClient(next)}
          onClose={onClose}
          save={save}
        />

        {rollup && <RollupBar />}
        <BuilderBanners
          noun={noun}
          save={save}
          error={error}
          notice={notice}
          onDismiss={() => setNotice(null)}
        />
        <div className={`rb-body${isReport ? ' rb-body-report' : ''}`}>
          {isReport && (
            <ReportPages
              pages={pages}
              active={pageIndex}
              editable={editable}
              onSelect={selectPage}
              onChange={changePages}
            />
          )}
          <main className="rb-main">
            {!isReport && (
              <PageTabs
                pages={pages}
                active={pageIndex}
                editable={editable}
                onSelect={selectPage}
                onChange={changePages}
              />
            )}
            <div
              className={`rb-stage rb-stage-${isReport ? initial.document.kind.toLowerCase() : device}`}
            >
              <ThemeScope theme={theme} layout={isReport ? 'report' : 'dashboard'}>
                <PageView
                  kind={initial.document.kind}
                  page={page}
                  pages={pages}
                  cover={cover}
                  canvas={{
                    widgets: shown,
                    results,
                    sources: catalogue.sources,
                    editable,
                    autoAlign,
                    selected,
                    keyOf,
                    onSelect: (index) => {
                      setSelected(index);
                      if (index !== null && editable) setPanel('settings');
                    },
                    onChange: setWidgets,
                    onRemove: widgetActions.remove,
                    onAddBelow: (index) => widgetActions.open(index),
                    renderMenu: (index, openFullScreen) => (
                      <WidgetMenu
                        {...widgetActions.menu(index, openFullScreen)}
                        onDownload={widgetDownload({
                          widget: shown[index],
                          result: results[keyOf(index)],
                          catalogue,
                          fileParts: [client.name, name],
                        })}
                      />
                    ),
                  }}
                />
              </ThemeScope>
              <EmptyPageHint
                show={editable && page.widgets.length === 0 && isGridPage}
                onAdd={() => widgetActions.open(null)}
              />
            </div>
            {editable && isGridPage && <AddWidgetButton onAdd={() => widgetActions.open(null)} />}
          </main>
          <BuilderPanels
            panel={panel}
            setPanel={setPanel}
            editable={editable}
            organisationId={organisationId}
            catalogue={catalogue}
            document={document}
            widgetActions={widgetActions}
            selected={selected}
            page={page}
            results={results}
            keyOf={keyOf}
            setWidgets={setWidgets}
            settings={settings}
            setSettings={setSettings}
            theme={theme}
            setTheme={setTheme}
            pages={pages}
            pageIndex={pageIndex}
            onGoToPage={selectPage}
            onOpenDocument={onOpenDocument}
            onRestored={(restored, restoredPages) => {
              setDocument(restored);
              setName(restored.name);
              setTheme(restored.theme as ReportTheme);
              history.set(restoredPages);
              setPageIndex(0);
            }}
          />
        </div>
        {presenting && (
          <PresentReport
            organisationId={organisationId}
            businessUnitId={document.business_unit_id}
            query={query}
            kind={initial.document.kind}
            pages={pages}
            start={pageIndex}
            cover={cover}
            sources={catalogue.sources}
            theme={theme}
            onClose={() => setPresenting(false)}
          />
        )}
      </div>
    </BuilderScope>
  );
}
